import {
  Box,
  MainButton,
  MainText,
  MainTitle,
  PressableOpacity,
} from "app/components/common";
import AnimatedBox from "app/components/common/AnimatedBox";
import i18n from "@microdoing/shared/i18n";
import React, { useEffect, useState } from "react";
import {
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  SafeAreaView,
  Pressable,
} from "react-native";
import Modal from "react-native-modal";
import CommentBox from "app/scenes/FeedbackScreen/CommentBox";
import { submitResourceFeedbackRequest } from "@microdoing/shared/services/coaching/actions";
import { connect, useDispatch, useSelector } from "react-redux";
import { getCoachingSessionId } from "@microdoing/shared/services/coaching/selectors";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import {
  getCampaignId,
  getUserCampaignId,
} from "@microdoing/shared/services/userCampaign/selectors";
import RatingStar from "../FeedbackScreen/ReflexiveAnalysis/ActionRatingScreen/RatingStar";
import { FullScreenModal } from "app/components/common/ModalFullScreen";
import normalize from "app/utils/normalize";
import { Linking } from "react-native";
import CrossIcon from "app/components/common/Icons/CrossIcon";
import theme from "app/Theme/Theme";

const FeedbackModal = ({
  isVisible,
  onPressModal,
  resourceId,
  userId, // doesn't use
  coachingSessionId, // doesn't use
  feedbackRequesting,
  isCoachingFeedback = false,
  link,
  type,
}) => {
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);

  const campaignId = useSelector(getCampaignId);
  const userCampaignId = useSelector(getUserCampaignId);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isVisible) {
      setComment("");
      setRating(0);
      setLoading(false);
    } else if (loading && !feedbackRequesting) {
      onPressModal();
    }
  }, [isVisible, loading, feedbackRequesting, onPressModal]);

  const onChangeComment = value => {
    setComment(value);
  };

  const onPressRating = value => {
    setRating(value);
  };

  const onSubmitFeedback = () => {
    setLoading(true);
    if (isCoachingFeedback) {
      dispatch(
        submitResourceFeedbackRequest({
          userId,
          resourceId,
          coachingSessionId,
          comment,
          rating,
        }),
      );
    } else {
      dispatch(
        submitResourceFeedbackRequest({
          userId,
          resourceId,
          userCampaignId,
          campaignId,
          comment,
          rating,
        }),
      );
    }
  };

  const goToRessource = () => {
    Linking.openURL(link);
  };

  return (
    <FullScreenModal isVisible={isVisible}>
      <ScrollView keyboardShouldPersistTaps="handled">
        <KeyboardAvoidingView
          keyboardShouldPersistTaps="handled"
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          keyboardVerticalOffset={Platform.OS === "ios" ? 50 : 76}
          enabled
        >
          <SafeAreaView>
            <Box flexDirection="row">
              <MainTitle
                style={{ maxWidth: "90%" }}
                variant="l7-bold"
                textAlign="left"
              >
                {i18n.t("what-did-you-think-resource")}
              </MainTitle>
              <PressableOpacity
                onPress={onPressModal}
                style={{
                  zIndex: 100,
                  position: "absolute",
                  right: theme.spacing.xxs,
                  borderRadius: normalize(8),
                  backgroundColor: theme.colors["brand.50"],
                  justifyContent: "center",
                  alignItems: "center",
                  padding: theme.spacing.xxs,
                }}
              >
                <CrossIcon
                  width={normalize(16)}
                  height={normalize(16)}
                  color={theme.colors["brand.500"]}
                />
              </PressableOpacity>
            </Box>
            <Pressable onPress={goToRessource}>
              <MainText variant="l3-bold" color="brand.500">
                {i18n.t("link-of-the-ressource")}
              </MainText>
            </Pressable>
            <MainText
              variant="l4-bold"
              color="neutral.900"
              marginBottom="xs"
              marginTop="xm"
            >
              {i18n.t("rate-this-ressource")}
            </MainText>
            <Box
              flexDirection="row"
              justifyContent="center"
              backgroundColor="neutral.50"
              borderRadius={200}
            >
              <Box paddingHorizontal="xs">
                <RatingStar
                  isFill={rating >= 1}
                  setRating={() => onPressRating(1)}
                  height={normalize(50)}
                  width={normalize(40)}
                />
              </Box>
              <Box paddingHorizontal="xs">
                <RatingStar
                  isFill={rating >= 2}
                  setRating={() => onPressRating(2)}
                  height={normalize(50)}
                  width={normalize(40)}
                />
              </Box>
              <Box paddingHorizontal="xs">
                <RatingStar
                  isFill={rating >= 3}
                  setRating={() => onPressRating(3)}
                  height={normalize(50)}
                  width={normalize(40)}
                />
              </Box>
              <Box paddingHorizontal="xs">
                <RatingStar
                  isFill={rating >= 4}
                  setRating={() => onPressRating(4)}
                  height={normalize(50)}
                  width={normalize(40)}
                />
              </Box>
              <Box paddingHorizontal="xxs">
                <RatingStar
                  isFill={rating >= 5}
                  setRating={() => onPressRating(5)}
                  height={normalize(50)}
                  width={normalize(40)}
                />
              </Box>
            </Box>
            <Box marginTop="sm" marginBottom="xs">
              <MainText variant="l3-medium" color="neutral.600">
                {i18n.t("how-did-you-find-the-ressource")}
              </MainText>
              <MainText variant="l3-medium" color="neutral.600">
                {i18n.t("did-you-find-the-ressource-easy-to-understand")}
              </MainText>
            </Box>
            <Box maxHeight={150} marginBottom="xs">
              <CommentBox
                onChangeText={onChangeComment}
                value={comment}
                placeholder={i18n.t("write-your-comment")}
              />
            </Box>
            <MainButton
              isLoading={feedbackRequesting}
              isDisabled={!rating}
              text={i18n.t("SUBMIT")}
              onPress={onSubmitFeedback}
            />
          </SafeAreaView>
        </KeyboardAvoidingView>
      </ScrollView>
    </FullScreenModal>
  );
};

const mapStateToProps = state => {
  return {
    coachingSessionId: getCoachingSessionId(state),
    userId: getUserId(state),
  };
};

export default connect(mapStateToProps, {})(FeedbackModal);
